import { c as _c } from "react/compiler-runtime";
import { Check, ChevronsUpDown, LucideIcon, Search } from "lucide-react";
import React, { useDeferredValue, useState, useEffect } from "react";
import { Button } from "@/src/components/shadcn/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/src/components/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { type EntityMatcherType, type EntityType, useEntityMatcherConfig } from "@/src/components/tasks/intake-sections/matcher-utils";
import { useExtractionAnnotationPopover } from "@/src/context/extraction-annotation-popover-context";
import { logger } from "@/src/lib/logging/logger";
import type { PayerWeighting } from "@/src/lib/payer-search";
import { cn } from "@/src/lib/shadcn-utils";
interface MatcherCommandItemProps {
  value: string;
  label: string;
  isSelected: boolean;
  onSelect: () => void;
  icon: LucideIcon;
  badge?: React.ReactNode;
  className?: string;
}
const MatcherCommandItem = t0 => {
  const $ = _c(20);
  const {
    value,
    icon: Icon,
    label,
    isSelected,
    onSelect,
    className,
    badge
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isSelected ? "opacity-100" : "opacity-0";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("mr-1 h-3 w-3 shrink-0", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Check className={t3} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== Icon) {
    t5 = <Icon className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
    $[6] = Icon;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== label) {
    t6 = <span className="truncate mr-2">{label}</span>;
    $[8] = label;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== badge || $[11] !== t6) {
    t7 = <div className="flex items-center w-full">{t6}{badge}</div>;
    $[10] = badge;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== onSelect || $[14] !== t1 || $[15] !== t4 || $[16] !== t5 || $[17] !== t7 || $[18] !== value) {
    t8 = <CommandItem value={value} onSelect={onSelect} className={t1}>{t4}{t5}{t7}</CommandItem>;
    $[13] = onSelect;
    $[14] = t1;
    $[15] = t4;
    $[16] = t5;
    $[17] = t7;
    $[18] = value;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
interface EntityMatcherProps<T extends EntityMatcherType> {
  // Entity type - determines configuration and external data sources
  type: EntityType;

  // Entities that are already persisted in the DB
  existingEntities?: T[] | undefined;
  isLoadingExistingEntities?: boolean;

  // Entity rendered as selected in the <Command> UI
  entity: T | null;
  onEntitySelected: (entity: T | null) => void;

  // Create new option
  showCreateNewOption?: boolean;
  weighting?: PayerWeighting;

  // Not listed option
  showNotListedOption?: boolean;
  isNotListedSelected?: boolean;
  onSelectNotListed?: () => void;

  // Match results from block matching (e.g., patient matching)
  // Structure: { entity: T | Partial<T>; confidence: number }[]
  matchResults?: Array<{
    entity: T | Partial<T>;
    confidence: number;
  }>;

  // Tab index for keyboard navigation
  tabIndex?: number;

  // Disable external search - if true, will not use external data sources (e.g., NPI, geocoding)
  disableExternalSearch?: boolean;

  // Restrict the dropdown to supplied match results plus the create-new option.
  restrictToMatchResults?: boolean;

  // Require an explicit selection even when the current entity has no persisted ID.
  requiresExplicitSelection?: boolean;
}
function EntityMatcher(t0) {
  const $ = _c(128);
  const {
    type,
    existingEntities,
    isLoadingExistingEntities,
    entity,
    onEntitySelected,
    showCreateNewOption: t1,
    weighting,
    matchResults,
    tabIndex,
    disableExternalSearch: t2,
    restrictToMatchResults: t3,
    requiresExplicitSelection: t4,
    showNotListedOption: t5,
    isNotListedSelected: t6,
    onSelectNotListed
  } = t0;
  const showCreateNewOption = t1 === undefined ? true : t1;
  const disableExternalSearch = t2 === undefined ? false : t2;
  const restrictToMatchResults = t3 === undefined ? false : t3;
  const requiresExplicitSelection = t4 === undefined ? false : t4;
  const showNotListedOption = t5 === undefined ? false : t5;
  const isNotListedSelected = t6 === undefined ? false : t6;
  const openExtractionAnnotation = useExtractionAnnotationPopover();
  const {
    search,
    displayConfig,
    externalDataSources,
    generateInitialSearchQuery
  } = useEntityMatcherConfig(type);
  let t7;
  if ($[0] !== disableExternalSearch || $[1] !== externalDataSources || $[2] !== restrictToMatchResults) {
    t7 = disableExternalSearch || restrictToMatchResults ? [] : externalDataSources;
    $[0] = disableExternalSearch;
    $[1] = externalDataSources;
    $[2] = restrictToMatchResults;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const filteredExternalDataSources = t7;
  let t8;
  if ($[4] !== entity || $[5] !== generateInitialSearchQuery) {
    t8 = entity && generateInitialSearchQuery ? generateInitialSearchQuery(entity) : "";
    $[4] = entity;
    $[5] = generateInitialSearchQuery;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const initialSearchQuery = t8;
  const [open, setOpen] = useState(false);
  const [commandSelection, setCommandSelection] = useState(null);
  const [searchInput, setSearchInput] = useState(initialSearchQuery);
  let t9;
  if ($[7] !== searchInput) {
    t9 = searchInput.trim();
    $[7] = searchInput;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const deferredSearch = useDeferredValue(t9);
  const entityId = entity?.id;
  const isCreateNewSelected = showCreateNewOption && !entityId && !requiresExplicitSelection;
  const entities = existingEntities ?? [];
  const searchResults = deferredSearch || weighting && weighting.length > 0 ? search(entities, deferredSearch || "", weighting) : entities.slice(0, 20);
  let t10;
  if ($[9] !== entityId || $[10] !== matchResults || $[11] !== searchInput) {
    t10 = new Set(!searchInput && matchResults ? matchResults.filter(match => {
      const matchEntity = match.entity as T;
      return matchEntity && "id" in matchEntity && matchEntity.id && matchEntity.id !== entityId;
    }).map(_temp).filter(_temp2) : []);
    $[9] = entityId;
    $[10] = matchResults;
    $[11] = searchInput;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const matchResultIds = t10;
  let t11;
  if ($[13] !== entityId || $[14] !== matchResultIds) {
    t11 = candidate => {
      if (entityId && candidate.id === entityId) {
        return false;
      }
      if (matchResultIds.has(candidate.id)) {
        return false;
      }
      return true;
    };
    $[13] = entityId;
    $[14] = matchResultIds;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const filteredEntities = (restrictToMatchResults ? [] : searchResults).filter(t11);
  const firstExisting = filteredEntities[0];
  let t12;
  if ($[16] !== filteredExternalDataSources) {
    t12 = filteredExternalDataSources.flatMap(_temp3);
    $[16] = filteredExternalDataSources;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const firstExternal = t12[0];
  let t13;
  if ($[18] !== matchResults) {
    t13 = matchResults?.find(_temp4)?.entity.id;
    $[18] = matchResults;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const firstMatchId = t13;
  const defaultCommandValue = firstMatchId ?? firstExisting?.id ?? firstExternal?.id ?? (showCreateNewOption ? "create-new" : "");
  const selectedCommandValue = commandSelection?.query === deferredSearch ? commandSelection.value : defaultCommandValue;
  let t14;
  if ($[20] !== entityId || $[21] !== initialSearchQuery) {
    t14 = nextOpen => {
      if (nextOpen && !entityId && initialSearchQuery) {
        setSearchInput(initialSearchQuery);
      }
      setOpen(nextOpen);
    };
    $[20] = entityId;
    $[21] = initialSearchQuery;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const handleOpenChange = t14;
  let t15;
  if ($[23] !== filteredExternalDataSources || $[24] !== onEntitySelected || $[25] !== openExtractionAnnotation) {
    t15 = selectedEntity => {
      onEntitySelected(selectedEntity);
      setOpen(false);
      openExtractionAnnotation?.();
      setSearchInput("");
      setCommandSelection(null);
      filteredExternalDataSources.forEach(_temp5);
    };
    $[23] = filteredExternalDataSources;
    $[24] = onEntitySelected;
    $[25] = openExtractionAnnotation;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const handleSelectEntity = t15;
  let t16;
  bb0: {
    if (!matchResults || matchResults.length === 0 || !restrictToMatchResults && searchInput) {
      let t17;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = [];
        $[27] = t17;
      } else {
        t17 = $[27];
      }
      t16 = t17;
      break bb0;
    }
    let t17;
    if ($[28] !== displayConfig || $[29] !== entityId || $[30] !== handleSelectEntity || $[31] !== matchResults) {
      let t18;
      if ($[33] !== entityId) {
        t18 = match_2 => {
          const matchEntity_1 = match_2.entity as T;
          return matchEntity_1 && "id" in matchEntity_1 && matchEntity_1.id && matchEntity_1.id !== entityId;
        };
        $[33] = entityId;
        $[34] = t18;
      } else {
        t18 = $[34];
      }
      let t19;
      if ($[35] !== displayConfig || $[36] !== entityId || $[37] !== handleSelectEntity) {
        t19 = match_3 => {
          const matchEntity_2 = match_3.entity as T;
          if (!matchEntity_2 || !("id" in matchEntity_2) || !matchEntity_2.id) {
            return null;
          }
          return <MatcherCommandItem key={matchEntity_2.id} value={matchEntity_2.id} icon={displayConfig.entityIcon} label={displayConfig.getPotentialMatchDisplayName?.(matchEntity_2) ?? displayConfig.getDisplayName(matchEntity_2)} isSelected={entityId === matchEntity_2.id} onSelect={() => handleSelectEntity(matchEntity_2)} />;
        };
        $[35] = displayConfig;
        $[36] = entityId;
        $[37] = handleSelectEntity;
        $[38] = t19;
      } else {
        t19 = $[38];
      }
      t17 = matchResults.filter(t18).map(t19).filter(_temp6);
      $[28] = displayConfig;
      $[29] = entityId;
      $[30] = handleSelectEntity;
      $[31] = matchResults;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    t16 = t17;
  }
  const validMatchResults = t16;
  let t17;
  if ($[39] !== deferredSearch || $[40] !== disableExternalSearch || $[41] !== externalDataSources || $[42] !== restrictToMatchResults) {
    t17 = () => {
      if (disableExternalSearch || restrictToMatchResults) {
        return;
      }
      externalDataSources.forEach(source_1 => {
        if (source_1.shouldShow(deferredSearch)) {
          ;
          try {
            source_1.search(deferredSearch);
          } catch (t18) {
            const error = t18;
            logger.error("Error searching external data source", {
              error
            });
          }
        } else {
          source_1.search("");
        }
      });
    };
    $[39] = deferredSearch;
    $[40] = disableExternalSearch;
    $[41] = externalDataSources;
    $[42] = restrictToMatchResults;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  useEffect(t17, [deferredSearch, disableExternalSearch, externalDataSources, filteredEntities.length, restrictToMatchResults]);
  if (isLoadingExistingEntities) {
    let t18;
    if ($[44] !== displayConfig.entityIcon) {
      t18 = <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />;
      $[44] = displayConfig.entityIcon;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== displayConfig.entityListTitle) {
      t19 = displayConfig.entityListTitle.toLowerCase();
      $[46] = displayConfig.entityListTitle;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    const t20 = `Loading ${t19}...`;
    let t21;
    if ($[48] !== t20) {
      t21 = <span className="truncate">{t20}</span>;
      $[48] = t20;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    let t22;
    if ($[50] !== t18 || $[51] !== t21) {
      t22 = <div className="min-w-0 flex-1 flex items-center">{t18}{t21}</div>;
      $[50] = t18;
      $[51] = t21;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    let t23;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    let t24;
    if ($[54] !== t22) {
      t24 = <div className="mb-2 mt-2 w-full"><Button variant="outline" disabled={true} className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm p-2 border-accent">{t22}{t23}</Button></div>;
      $[54] = t22;
      $[55] = t24;
    } else {
      t24 = $[55];
    }
    return t24;
  }
  const t18 = "mb-2 mt-2 w-full";
  const T0 = Popover;
  let t19;
  if ($[56] !== displayConfig || $[57] !== entity || $[58] !== isNotListedSelected || $[59] !== showCreateNewOption) {
    t19 = isNotListedSelected ? <><displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">Not Listed</span></> : !entity ? <>{showCreateNewOption ? <><displayConfig.createNewIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{displayConfig.createNewTitle}</span></> : <><Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{displayConfig.placeholder}</span></>}</> : entity ? <><displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{displayConfig.getDisplayName(entity)}</span><div className="ml-auto">{displayConfig?.renderBadge?.(entity)}</div></> : null;
    $[56] = displayConfig;
    $[57] = entity;
    $[58] = isNotListedSelected;
    $[59] = showCreateNewOption;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== t19) {
    t20 = <div className="min-w-0 flex-1 flex items-center">{t19}</div>;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== open || $[65] !== t20 || $[66] !== tabIndex) {
    t22 = <PopoverTrigger asChild={true}><Button variant="outline" role="combobox" aria-expanded={open} tabIndex={tabIndex} className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm hover:bg-accent p-2 border-accent">{t20}{t21}</Button></PopoverTrigger>;
    $[64] = open;
    $[65] = t20;
    $[66] = tabIndex;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  const T1 = PopoverContent;
  const t23 = "w-[var(--radix-popover-trigger-width)] min-w-[400px] p-0 border-muted/50 rounded-sm shadow-sm bg-popover";
  const T2 = Command;
  const t24 = "w-full";
  const t25 = false;
  let t26;
  if ($[68] !== deferredSearch) {
    t26 = value => {
      setCommandSelection({
        query: deferredSearch,
        value
      });
    };
    $[68] = deferredSearch;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== displayConfig || $[71] !== open || $[72] !== restrictToMatchResults || $[73] !== searchInput) {
    t27 = !restrictToMatchResults && <CommandInput placeholder={displayConfig.placeholder} value={searchInput} onValueChange={value_0 => {
      setSearchInput(value_0);
      if (!open) {
        setOpen(true);
      }
    }} />;
    $[70] = displayConfig;
    $[71] = open;
    $[72] = restrictToMatchResults;
    $[73] = searchInput;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  const T3 = CommandList;
  let t28;
  if ($[75] !== displayConfig || $[76] !== entity || $[77] !== handleSelectEntity || $[78] !== searchInput) {
    t28 = entity?.id && !searchInput && <CommandGroup heading="Currently selected"><MatcherCommandItem key={entity.id} value={entity.id} icon={displayConfig.entityIcon} label={displayConfig.getDisplayName(entity)} isSelected={true} onSelect={() => handleSelectEntity(entity)} badge={displayConfig.renderBadge && displayConfig.renderBadge(entity)} /></CommandGroup>;
    $[75] = displayConfig;
    $[76] = entity;
    $[77] = handleSelectEntity;
    $[78] = searchInput;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== validMatchResults) {
    t29 = validMatchResults.length > 0 && <CommandGroup heading="Potential matches">{validMatchResults}</CommandGroup>;
    $[80] = validMatchResults;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  const t30 = existingEntities?.length ? <CommandGroup heading={displayConfig.entityListTitle}>{filteredEntities.length ? <>{filteredEntities.map(existingEntity => <MatcherCommandItem key={existingEntity.id} value={existingEntity.id} icon={displayConfig.entityIcon} label={displayConfig.getDisplayName(existingEntity)} isSelected={entity?.id === existingEntity.id} onSelect={() => handleSelectEntity(existingEntity as T)} badge={displayConfig.renderBadge && displayConfig.renderBadge(existingEntity)} />)}</> : <div className="px-6 py-1.5 text-sm">No matching existing {displayConfig.entityListTitle.toLowerCase()} found.</div>}</CommandGroup> : null;
  let t31;
  if ($[82] !== deferredSearch || $[83] !== displayConfig || $[84] !== entity?.id || $[85] !== filteredExternalDataSources || $[86] !== handleSelectEntity) {
    let t32;
    if ($[88] !== deferredSearch || $[89] !== displayConfig || $[90] !== entity?.id || $[91] !== handleSelectEntity) {
      t32 = (source_2, index) => {
        const sourceResults = source_2.results;
        const shouldShow = source_2.shouldShow(deferredSearch);
        const title = !source_2.isLoading && !source_2.error && sourceResults?.length ? source_2.groupTitle + " (" + sourceResults.length + " results)" : source_2.groupTitle;
        if (!shouldShow) {
          return null;
        }
        return <CommandGroup key={`external-${index}`} heading={title}>{source_2.isLoading ? <div className="px-6 py-1.5 text-sm">Searching...</div> : source_2.error ? <div className="px-6 py-1.5 text-sm">{source_2.error}</div> : sourceResults?.length ? sourceResults.map(externalEntity => <MatcherCommandItem key={`external-${index}-${externalEntity.id}`} value={externalEntity.id} icon={source_2.getIcon(externalEntity)} label={source_2.getDisplayName(externalEntity)} isSelected={entity?.id === externalEntity.id} onSelect={() => handleSelectEntity(externalEntity as T)} badge={displayConfig?.renderBadge?.(externalEntity)} />) : <div className="px-6 py-1.5 text-sm">No matching {source_2.groupTitle} found.</div>}</CommandGroup>;
      };
      $[88] = deferredSearch;
      $[89] = displayConfig;
      $[90] = entity?.id;
      $[91] = handleSelectEntity;
      $[92] = t32;
    } else {
      t32 = $[92];
    }
    t31 = filteredExternalDataSources.map(t32);
    $[82] = deferredSearch;
    $[83] = displayConfig;
    $[84] = entity?.id;
    $[85] = filteredExternalDataSources;
    $[86] = handleSelectEntity;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  let t32;
  if ($[93] !== displayConfig || $[94] !== isNotListedSelected || $[95] !== onSelectNotListed || $[96] !== openExtractionAnnotation || $[97] !== showNotListedOption) {
    t32 = showNotListedOption && <CommandGroup><MatcherCommandItem key="not-listed" value="not-listed" icon={displayConfig.entityIcon} label="Not Listed" isSelected={isNotListedSelected} onSelect={() => {
        setOpen(false);
        onSelectNotListed?.();
        openExtractionAnnotation?.();
      }} /></CommandGroup>;
    $[93] = displayConfig;
    $[94] = isNotListedSelected;
    $[95] = onSelectNotListed;
    $[96] = openExtractionAnnotation;
    $[97] = showNotListedOption;
    $[98] = t32;
  } else {
    t32 = $[98];
  }
  let t33;
  if ($[99] !== displayConfig || $[100] !== handleSelectEntity || $[101] !== isCreateNewSelected || $[102] !== requiresExplicitSelection || $[103] !== showCreateNewOption) {
    t33 = showCreateNewOption && <CommandGroup heading="Create"><MatcherCommandItem key="create-new" value="create-new" icon={displayConfig.createNewIcon} label={displayConfig.createNewTitle} isSelected={isCreateNewSelected} onSelect={() => {
        if (isCreateNewSelected && !requiresExplicitSelection) {
          setOpen(false);
          return;
        }
        handleSelectEntity(null);
      }} /></CommandGroup>;
    $[99] = displayConfig;
    $[100] = handleSelectEntity;
    $[101] = isCreateNewSelected;
    $[102] = requiresExplicitSelection;
    $[103] = showCreateNewOption;
    $[104] = t33;
  } else {
    t33 = $[104];
  }
  let t34;
  if ($[105] !== T3 || $[106] !== t28 || $[107] !== t29 || $[108] !== t30 || $[109] !== t31 || $[110] !== t32 || $[111] !== t33) {
    t34 = <T3>{t28}{t29}{t30}{t31}{t32}{t33}</T3>;
    $[105] = T3;
    $[106] = t28;
    $[107] = t29;
    $[108] = t30;
    $[109] = t31;
    $[110] = t32;
    $[111] = t33;
    $[112] = t34;
  } else {
    t34 = $[112];
  }
  let t35;
  if ($[113] !== T2 || $[114] !== selectedCommandValue || $[115] !== t26 || $[116] !== t27 || $[117] !== t34) {
    t35 = <T2 className={t24} shouldFilter={t25} value={selectedCommandValue} onValueChange={t26}>{t27}{t34}</T2>;
    $[113] = T2;
    $[114] = selectedCommandValue;
    $[115] = t26;
    $[116] = t27;
    $[117] = t34;
    $[118] = t35;
  } else {
    t35 = $[118];
  }
  let t36;
  if ($[119] !== T1 || $[120] !== t35) {
    t36 = <T1 className={t23}>{t35}</T1>;
    $[119] = T1;
    $[120] = t35;
    $[121] = t36;
  } else {
    t36 = $[121];
  }
  let t37;
  if ($[122] !== T0 || $[123] !== handleOpenChange || $[124] !== open || $[125] !== t22 || $[126] !== t36) {
    t37 = <div className={t18}><T0 open={open} onOpenChange={handleOpenChange}>{t22}{t36}</T0></div>;
    $[122] = T0;
    $[123] = handleOpenChange;
    $[124] = open;
    $[125] = t22;
    $[126] = t36;
    $[127] = t37;
  } else {
    t37 = $[127];
  }
  return t37;
}
function _temp6(item) {
  return item !== null;
}
function _temp5(source_0) {
  source_0.search("");
}
function _temp4(match_1) {
  return match_1.entity.id;
}
function _temp3(source) {
  return source.results;
}
function _temp2(id) {
  return id !== null;
}
function _temp(match_0) {
  const matchEntity_0 = match_0.entity as T;
  return matchEntity_0 && "id" in matchEntity_0 ? matchEntity_0.id : null;
}
export { EntityMatcher };